import { Matrix4, Vector3 } from 'three';
import { CommonConstants } from '../constants/common-constants';
import { MathUtils } from '../common/util/math-utils';

export class Line {
  private startPoint: Vector3;
  private endPoint: Vector3;

  public constructor(p1: Vector3, p2: Vector3) {
    this.startPoint = p1;
    this.endPoint = p2;
  }

  public get start(): Vector3 {
    return this.startPoint;
  }

  public get end(): Vector3 {
    return this.endPoint;
  }

  public get length(): number {
    return this.end.distanceTo(this.start);
  }

  public split(point: Vector3): Line[] {
    return [new Line(this.startPoint, point), new Line(point, this.endPoint)];
  }

  public hasPoint(point: Vector3): boolean {
    const sD: number = this.start.distanceTo(point);
    const eD: number = this.end.distanceTo(point);
    const d: number = this.length + CommonConstants.EPSILON * 2;
    return (sD <= d && eD <= d);
  }

  public closeToStart(point: Vector3): boolean {
    const sD: number = this.start.distanceTo(point);
    const eD: number = this.end.distanceTo(point);
    return sD < eD;
  }

  public closeToEnd(point: Vector3): boolean {
    const sD: number = this.start.distanceTo(point);
    const eD: number = this.end.distanceTo(point);
    return sD > eD;
  }

  public hasLine(line: Line, fullInclusion: boolean = true): boolean {
    return fullInclusion ?
      this.hasPoint(line.start) && this.hasPoint(line.end) :
      this.hasPoint(line.start) || this.hasPoint(line.end);
  }

  public move(delta: Vector3): Line {
    const sPos: Vector3 = this.start.clone();
    sPos.add(delta);

    const ePos: Vector3 = this.end.clone();
    ePos.add(delta);

    return new Line(sPos, ePos);
  }

  public transform(translationMatrix: Matrix4): Line {
    let sPos: Vector3 = this.start.clone();
    sPos = MathUtils.transform(sPos, translationMatrix);

    let ePos: Vector3 = this.end.clone();
    ePos = MathUtils.transform(ePos, translationMatrix);

    return new Line(sPos, ePos);
  }

  public reverse(): Line {
    const newLine: Line = new Line(this.endPoint, this.startPoint);
    this.startPoint = newLine.start;
    this.endPoint = newLine.end;
    return newLine;
  }
}
